import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { config } from 'env';
import { analytics } from 'lib/analytics';
import { ExitAnswer, exitComment, exitReasonLabel } from './reasons';

// The PostHog survey asks the reason, then the note; its question ids live in config in that order.
const SURVEY_QUESTION_COUNT = 2;

type CancellationSurvey = {
  /** Joins the leave events to this visit's `survey sent`; absent when no survey is configured. */
  submissionId?: string;
  /** Records the answer. Called only once the cancellation succeeds, so responses are real cancellations. */
  send: (answer: ExitAnswer) => void;
  /** Closes the survey unanswered, for a customer who leaves without cancelling. */
  dismiss: () => void;
};

/**
 * The PostHog cancellation survey behind the reason step: shown once while `active`; the page decides
 * when it is sent or dismissed. Silent unless the survey is fully configured.
 */
export function useCancellationSurvey(active: boolean): CancellationSurvey {
  const { id, question_ids: questionIds } = config.posthog_cancellation_survey;
  const surveyId = id && questionIds.length === SURVEY_QUESTION_COUNT ? id : '';
  // One per visit, so it can link the survey answer to the leave events.
  const [submissionId] = useState(uuidv4);
  const progress = useRef({ shown: false, closed: false });

  useEffect(() => {
    if (!surveyId || !active || progress.current.shown) return;
    progress.current.shown = true;
    analytics.surveyShown(surveyId);
  }, [surveyId, active]);

  const send = useCallback(
    (answer: ExitAnswer) => {
      if (!surveyId || progress.current.closed) return;
      progress.current.closed = true;
      analytics.surveySent({
        surveyId,
        submissionId,
        questionIds,
        responses: [exitReasonLabel(answer.reason), exitComment(answer) || null]
      });
    },
    [surveyId, submissionId, questionIds]
  );

  const dismiss = useCallback(() => {
    if (!surveyId || !progress.current.shown || progress.current.closed) return;
    progress.current.closed = true;
    analytics.surveyDismissed(surveyId);
  }, [surveyId]);

  return { submissionId: surveyId ? submissionId : undefined, send, dismiss };
}
